/**
 * Frontend mirror of the backend super-admin destructive-op guard
 * (services/project-tracker/backend/utils/super_admin_destructive_guard.py).
 *
 * The backend gates a super-admin on a production tenant for two op classes:
 *   1. Invoice status DOWNGRADES — paid/partially_paid → sent → ready → draft,
 *      OR any of {paid, partially_paid, sent, ready} → cancelled.
 *   2. Payment VOIDS (DELETE /api/invoices/{id}/payments).
 * For those ops it requires the X-Super-Admin-Confirm-Destructive header
 * carrying the exact invoice id (else 403) and rate-limits to 10 ops / 60s.
 *
 * UPGRADES (draft→ready/sent, sent→paid) are normal billing flow and are NOT
 * guarded — the FE must not show the confirm modal for them, or it would nag on
 * every routine status bump.
 *
 * This module is the single source of truth the UI uses to decide whether a
 * super-admin's status change needs the confirm step. It intentionally
 * duplicates the backend's rank map so the two stay aligned; keep them in sync.
 */

// Mirrors super_admin_destructive_guard.py `rank` (note: paid and
// partially_paid share rank 3, so paid↔partially_paid is NOT a downgrade).
const STATUS_RANK: Record<string, number> = {
  draft: 0,
  ready: 1,
  sent: 2,
  paid: 3,
  partially_paid: 3,
};

const CANCEL_SOURCE_STATUSES = new Set(['paid', 'partially_paid', 'sent', 'ready']);

/**
 * Returns true when transitioning `oldStatus` → `newStatus` is a guarded
 * status downgrade (or guarded cancel) — i.e. the exact set the backend gates
 * for super-admins on production tenants. Mirrors guard_invoice_update().
 */
export function isGuardedStatusDowngrade(
  oldStatus: string | undefined | null,
  newStatus: string | undefined | null,
): boolean {
  if (!oldStatus || !newStatus || oldStatus === newStatus) return false;

  const isCancel = newStatus === 'cancelled' && CANCEL_SOURCE_STATUSES.has(oldStatus);
  if (isCancel) return true;

  const oldRank = STATUS_RANK[oldStatus];
  const newRank = STATUS_RANK[newStatus];
  if (oldRank === undefined || newRank === undefined) return false;
  return newRank < oldRank;
}

/** Human label for a status, e.g. 'partially_paid' → 'Partially Paid'. */
export function statusLabel(status: string): string {
  return status
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}
